'use client';

import type { Yarn } from '@/lib/types';
import YarnSwatch from './YarnSwatch';

/**
 * YarnTile — the rich multi-colorway thumbnail used on the left side
 * of alphabet-grouped yarn rows (the "big left photo tile" treatment).
 *
 * Extracted in P0 structural follow-up (build 1418) from yarn/page.tsx.
 * Uses an absolute-positioned inner grid so the tile contributes zero
 * intrinsic height (right column dictates row height).
 *
 * Part of the two coordinated yarn card patterns documented in the
 * styleguide (see "Yarn Card Patterns (library-specific)").
 *
 * Only used by the alphabet-mode path. Color/brand/weight modes use the
 * simpler YarnCard + YarnGroupCard via YarnBrowser.
 */

export const TILE_PLACEMENTS: Record<number, React.CSSProperties[]> = {
  1: [{ gridColumn: '1 / span 2', gridRow: '1 / span 2' }],
  2: [
    { gridColumn: '1 / span 2', gridRow: 1 },
    { gridColumn: '1 / span 2', gridRow: 2 },
  ],
  3: [
    { gridColumn: 1, gridRow: 1 },
    { gridColumn: 2, gridRow: '1 / span 2' },
    { gridColumn: 1, gridRow: 2 },
  ],
  4: [
    { gridColumn: 1, gridRow: 1 },
    { gridColumn: 2, gridRow: 1 },
    { gridColumn: 1, gridRow: 2 },
    { gridColumn: 2, gridRow: 2 },
  ],
};

interface YarnTileProps {
  yarns: Yarn[];
  borderColor: string;
}

export function YarnTile({ yarns, borderColor }: YarnTileProps) {
  const visible = yarns.slice(0, 4);
  const placements = TILE_PLACEMENTS[visible.length] ?? TILE_PLACEMENTS[4];

  return (
    <div
      style={{
        position: 'relative',
        boxSizing: 'content-box',
        width: 88,
        alignSelf: 'stretch',
        flexShrink: 0,
        borderRight: `1px solid ${borderColor}`,
        background: visible[0]?.colorHex || '#E8E4DF',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: '1fr 1fr',
        }}
      >
        {visible.map((y, i) => (
          <div
            key={y.id}
            style={{ ...placements[i], minHeight: 0, minWidth: 0, overflow: 'hidden' }}
          >
            <YarnSwatch
              yarn={y}
              ariaLabel={y.colorName}
              className="block w-full h-full"
              style={{ objectFit: 'cover' }}
              lazy
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default YarnTile;